import React from 'react';
import { PropTypes } from 'prop-types';
import styled from 'styled-components';
import Constants from '../../submodules/logictry_config/constants';
import Navigation from '../../services/Navigation';
import WindowSize from '../../services/WindowSize';
import { ArrowDownIcon, ArrowUpIcon, DeleteIcon } from '../../styledhtml/Icon';
import ConfirmDialog from '../ConfirmDialog';
import { TabBar } from '../UnderlineTabs';
import LogictryComponent from '../../submodules/logictry_config/LogictryComponent';
import ContentRailCard from '../ContentRailCard';
import CreateContentPopupService from '../../services/CreateContentPopup';
import { CollectionGrid } from '../../styledhtml/LogicBaseHomeLayout';
import {
  COLLECTION_GRID_MAX,
  RAIL_CARD_WIDTH,
  RAIL_CARD_WIDTH_MOBILE,
  RAIL_CARD_WIDTH_TABLET,
} from '../../styledhtml/LogicBaseCard';
const Wrapper = styled.div`
  display: flex;
`;

const Row = styled.div`
  display: flex;
  align-items: stretch;
`;

const CardSlot = styled.div`
  ${(p) => (p.$fill ? `
    flex: 1 1 auto;
    min-width: 0;
  ` : `
    flex: 0 0 auto;
  `)}
`;

const AppSort = styled.div`
  display: flex;
  align-items: center;
  flex-direction: column;
  justify-content: center;
  flex-shrink: 0;

  > div {
    width: 32px;
    height: 32px;
    background-size: auto 50%;
  }
`;

const CreateTile = styled.div`
  position: relative;
  flex-shrink: 0;
  cursor: pointer;
  box-sizing: border-box;
  border-radius: 12px;
  border: 1.5px dashed ${Constants.DashedBorderColor};
  background: ${Constants.SurfaceColor};
  display: flex;
  flex-direction: column;
  overflow: hidden;
  align-self: start;
  color: ${Constants.MediumTextGray};
  transition: border-color 0.15s ease, color 0.15s ease, background 0.15s ease;
  &:hover {
    border-color: ${Constants.PrimaryColor};
    color: ${Constants.PrimaryColor};
    background: ${Constants.SurfaceHover};
  }
`;

const CreatePhoto = styled.div`
  width: 100%;
  aspect-ratio: 16 / 9;
  position: relative;
  flex-shrink: 0;
`;

/** Matches `ContentRailCard` caption (incl. its -4.5% pull-up) so + New Content tiles share rail/grid height. */
const CreateCaption = styled.div`
  flex-shrink: 0;
  margin-top: -4.5%;
  padding: 0.4rem 0.65rem 0.45rem;
  ${Constants.MediaTabletAndDesktop} {
    padding: 0.45rem 0.7rem 0.5rem;
  }
`;

const CreateCaptionKicker = styled.div`
  height: calc(0.5625rem * 1.2);
  margin: 0 0 0.28rem;
`;

const CreateCaptionTitle = styled.div`
  height: calc(0.8125rem * 1.15);
  ${Constants.MediaTabletAndDesktop} {
    height: calc(0.875rem * 1.15);
  }
`;

const CreateCaptionMeta = styled.div`
  height: calc(0.6875rem * 1.25);
  margin: 0.28rem 0 0;
`;

const CreateLabel = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  gap: 0.4rem;
`;

const CreatePlus = styled.span`
  font-size: 1.5em;
  line-height: 1;
  font-weight: 400;
`;

function swapIdsInList(ids, appId, neighborId) {
  if (!ids?.length) return null;
  const index = ids.findIndex((id) => String(id) === String(appId));
  const neighborIndex = ids.findIndex((id) => String(id) === String(neighborId));
  if (index < 0 || neighborIndex < 0) return null;
  const next = ids.slice();
  [next[index], next[neighborIndex]] = [next[neighborIndex], next[index]];
  return next;
}

function relatedRailScrollLeft() {
  const historyState = window.history?.state || {};
  const internalState = historyState.state || historyState.usr || {};
  if ('logicbaseapp_related_horizontal' in historyState) {
    return historyState.logicbaseapp_related_horizontal;
  }
  if (internalState && 'logicbaseapp_related_horizontal' in internalState) {
    return internalState.logicbaseapp_related_horizontal;
  }
  return undefined;
}

function horizontalScrollParent(el) {
  let node = el?.parentElement;
  while (node && node !== document.body) {
    const { overflowX } = window.getComputedStyle(node);
    if (overflowX === 'auto' || overflowX === 'scroll') return node;
    node = node.parentElement;
  }
  return null;
}

export default class LogicBaseAppList extends LogictryComponent {
  static propTypes = {
    apps: PropTypes.array,
    listView: PropTypes.bool,
    small: PropTypes.bool,
    collection: PropTypes.object,
    allowSorting: PropTypes.bool,
    horizontal: PropTypes.bool,
    wrap: PropTypes.bool,
    showCreateNew: PropTypes.bool,
    appTopics: PropTypes.object,
    onTopicSelected: PropTypes.func,
    captionTitle: PropTypes.bool,
  }
  state = {
    appId: Navigation.currentLocation[2],
    q: Navigation.currentLocation[1] === 'search' && Navigation.currentLocation[2] && unescape(Navigation.currentLocation[2]) || Navigation.currentSearch.q || '',
    collectionParam: Navigation.currentSearch.collection,
    confirmRemoveApp: false,
  }
  componentDidMount() {
    WindowSize.onStateUpdate(this);
    window.addEventListener('resize', this.resized);
    this.scheduleScrollCurrentIntoView();
  }
  componentDidUpdate() {
    this.scheduleScrollCurrentIntoView();
  }
  componentWillUnmount() {
    WindowSize.offStateUpdate(this);
    window.removeEventListener('resize', this.resized);
    cancelAnimationFrame(this._scrollCurrentRaf);
  }
  resized = () => {
    this.forceUpdate();
  }
  scheduleScrollCurrentIntoView = () => {
    if (this._didScrollCurrentIntoView) return;
    cancelAnimationFrame(this._scrollCurrentRaf);
    this._scrollCurrentRaf = requestAnimationFrame(this.scrollCurrentIntoView);
  }
  /** Fresh content landings have no related-rail offset — center the current card in the rail. */
  scrollCurrentIntoView = () => {
    if (this._didScrollCurrentIntoView) return;
    if (Navigation.currentLocation[1] !== 'content') return;
    const { listView, wrap } = this.props;
    if (listView || wrap) return;
    if (!this.state.appId) return;
    // Content → content copies the live offset (including 0). Only center on
    // a fresh landing from home/collection, where that key is absent.
    if (relatedRailScrollLeft() != null) {
      this._didScrollCurrentIntoView = true;
      return;
    }
    const currentEl = this.ref?.querySelector?.('[aria-current="true"]');
    if (!currentEl) return;
    const scroller = horizontalScrollParent(currentEl);
    if (!scroller) return;
    const elRect = currentEl.getBoundingClientRect();
    const scrollerRect = scroller.getBoundingClientRect();
    if (scrollerRect.width < 1 || elRect.width < 1) return;
    const elCenter = elRect.left + elRect.width / 2;
    const scrollerCenter = scrollerRect.left + scrollerRect.width / 2;
    scroller.scrollLeft += elCenter - scrollerCenter;
    this._didScrollCurrentIntoView = true;
  }
  /** Navigation for an app tile — preserves widget/cross-site/same-site behaviour that `ContentRailCard` doesn't know about. */
  openApp = (app) => {
    const { collection } = this.props;
    const { collectionParam, appId } = this.state;
    const { _id } = app;
    if (Constants.isWidget) {
      const params = new URLSearchParams(window.location.search);
      if (collectionParam) params.set('collection', collectionParam);
      Navigation.navigate(`${Navigation.content}/${_id}?${params.toString()}`);
      return;
    }
    const relativeUrl = collectionParam && `${Navigation.content}/${_id}?collection=${collectionParam}`
      || collection?._id && `${Navigation.content}/${_id}?collection=${collection._id}`
      || `${Navigation.content}/${_id}`;
    if (!Constants.isLogicWiki) {
      Navigation.site(`https://logictry.com${Navigation.content}/${_id}`);
      return;
    }
    const isCurrentApp = appId === _id;
    if (isCurrentApp) return;
    // Home/collection landings omit the related-rail offset so the next page
    // can center the current card. Content → content copies the live scroll.
    if (Navigation.currentLocation[1] !== 'content') {
      Navigation.push(relativeUrl);
      return;
    }
    const scroller = this.ref && horizontalScrollParent(this.ref);
    const historyState = window.history?.state || {};
    const internalState = historyState.state || historyState.usr || {};
    const logicbaseapp_related_horizontal = scroller != null
      ? scroller.scrollLeft
      : (historyState.logicbaseapp_related_horizontal
        ?? internalState.logicbaseapp_related_horizontal
        ?? 0);
    const logicbaseapp_related_loaded = historyState.logicbaseapp_related_loaded
      ?? internalState.logicbaseapp_related_loaded;
    Navigation.push(relativeUrl, {
      logicbaseapp_related_horizontal,
      ...(logicbaseapp_related_loaded != null ? { logicbaseapp_related_loaded } : {}),
    });
  }
  swapAppsInCollection = (appId, neighborId) => {
    const { collection } = this.props;
    if (!collection?.apps) return false;
    const next = swapIdsInList(collection.apps, appId, neighborId);
    if (!next) return false;
    collection.apps = next;
    return true;
  }
  removeAppFromCollection = (appId) => {
    const { collection } = this.props;
    if (!collection?.apps) return false;
    collection.apps = collection.apps.filter((id) => String(id) !== String(appId));
    return true;
  }
  appCard = (app, i, { fullWidth } = {}) => {
    const { apps, collection, allowSorting, appTopics, onTopicSelected, captionTitle, listView, wrap, small } = this.props;
    const { collectionParam, appId } = this.state;
    const { _id } = app;
    const topicKeys = appTopics ? Object.keys(appTopics) : [];
    const selectedTopic =
      allowSorting && topicKeys.length > 0
        ? topicKeys.find((k) => appTopics[k].includes(_id)) || ''
        : '';
    const isCurrentApp = appId === _id;
    const dimOthers = !listView && Navigation.currentLocation[1] === 'content' && !!appId;
    const fillRow = !!fullWidth || !!listView;
    const { width } = this.getSize();
    const cardStyle = {
      ...(fillRow ? {} : { width }),
    };

    return (
      <div
        key={_id}
        style={{
          ...(fillRow ? { minWidth: 0, width: '100%', maxWidth: wrap ? COLLECTION_GRID_MAX : '100%' } : undefined),
          ...(dimOthers ? { opacity: isCurrentApp ? 1 : 0.48, transition: 'opacity 0.15s ease' } : undefined),
        }}
      >
        <Row
          style={{
            flexDirection: allowSorting ? 'row' : 'column',
            gap: allowSorting ? '0.5rem' : undefined,
            width: fillRow ? '100%' : undefined,
            minWidth: fillRow ? 0 : undefined,
          }}
        >
          {allowSorting && <AppSort>
            {i > 0 && <ArrowUpIcon
              onClick={() => {
                const neighbor = apps[i - 1];
                if (!neighbor?._id || !this.swapAppsInCollection(_id, neighbor._id)) return;
                apps.splice(i, 1);
                apps.splice(i - 1, 0, app);
                this.forceUpdate();
              }}
            />}
            {i < apps.length - 1 && <ArrowDownIcon
              onClick={() => {
                const neighbor = apps[i + 1];
                if (!neighbor?._id || !this.swapAppsInCollection(_id, neighbor._id)) return;
                apps.splice(i, 1);
                apps.splice(i + 1, 0, app);
                this.forceUpdate();
              }}
            />}
          </AppSort>}
          <CardSlot $fill={fillRow}>
            <ContentRailCard
              tree={app}
              collectionId={collectionParam || collection?._id}
              onClick={() => this.openApp(app)}
              style={cardStyle}
              fullWidth={fillRow}
              captionTitle={!!captionTitle}
              small={!!small}
              kicker={collection?.title}
              current={isCurrentApp}
            />
          </CardSlot>
          {allowSorting && <AppSort>
            <DeleteIcon
              onClick={() => { this.setState({ confirmRemoveApp: app }) }}
            />
          </AppSort>}
        </Row>
        {allowSorting && topicKeys.length > 0 && <div style={{ flexShrink: 0, marginLeft: 0, marginTop: '0.65rem', minWidth: 0 }}>
          <TabBar
            variant="pill"
            ariaLabel="Content topics"
            items={topicKeys.map((key) => ({ id: key, label: key }))}
            value={selectedTopic}
            onChange={(topic) => { onTopicSelected(app, topic); }}
            rememberScrollPosition={`logicbaseapplist_filterbar_${_id}`}
          />
        </div>}
      </div>
    )
  }
  getSize = () => {
    const { small, wrap } = this.props;
    const { mobile, tablet } = WindowSize;
    /** Wrapped collection grids stretch tiles to fill each row; rails use fixed breakpoint widths. */
    if (small) return { width: mobile ? 160 : 178 };
    if (wrap) return { width: resizeItems(this.ref, 288) };
    if (mobile) return { width: RAIL_CARD_WIDTH_MOBILE };
    if (tablet) return { width: RAIL_CARD_WIDTH_TABLET };
    return { width: RAIL_CARD_WIDTH };
  }
  updatedRef = (e) => {
    if (this.ref !== e) {
      this.ref = e;
      this.forceUpdate();
    }
  }
  render() {
    const { apps, listView, small, wrap, showCreateNew, allowSorting, collection, captionTitle } = this.props;
    const { confirmRemoveApp } = this.state
    const { width } = this.getSize();
    const confirmDialog = confirmRemoveApp ? (
      <ConfirmDialog
        text="Confirm Remove"
        description="Are you sure you wish to remove?"
        open
        onNo={() => this.setState({ confirmRemoveApp: false })}
        onYes={() => {
          const appIndex = apps.findIndex(({ _id }) => _id === confirmRemoveApp._id);
          if (appIndex < 0) return;
          if (!this.removeAppFromCollection(confirmRemoveApp._id)) return;
          apps.splice(appIndex, 1);
          this.setState({ confirmRemoveApp: false });
        }}
      />
    ) : null;
    const createTile = showCreateNew ? (
      <CreateTile
        onClick={() => CreateContentPopupService.open({ collection })}
        style={{
          width: wrap ? '100%' : width,
          maxWidth: wrap ? COLLECTION_GRID_MAX : undefined,
          fontSize: small ? Constants.SmallFontSize : Constants.NormalFontSize,
        }}
      >
        <CreatePhoto />
        {!!captionTitle && (
          <CreateCaption aria-hidden>
            <CreateCaptionKicker />
            <CreateCaptionTitle />
            <CreateCaptionMeta />
          </CreateCaption>
        )}
        <CreateLabel>
          <CreatePlus>+</CreatePlus>
          New Content
        </CreateLabel>
      </CreateTile>
    ) : null;
    if (wrap && !listView && !allowSorting) {
      return (
        <>
          <CollectionGrid $flush>
            {createTile}
            {apps.map((app, i) => this.appCard(app, i, { fullWidth: true }))}
          </CollectionGrid>
          {confirmDialog}
        </>
      );
    }
    return (
      <Wrapper
        ref={this.updatedRef}
        style={{
          flexWrap: wrap && 'wrap',
          flexDirection: listView && 'column',
          alignItems: listView ? 'stretch' : 'flex-start',
          width: listView ? '100%' : undefined,
          gap: (allowSorting && listView && '1rem') || (allowSorting && '2rem') || (small && (wrap && '1rem 0.75rem' || '0.75rem')) || (wrap && '1.5rem') || '1.2rem'
        }}
      >
        {createTile}
        {apps.map((app, i) => this.appCard(app, i))}
        {confirmDialog}
      </Wrapper>
    );
  }
}
function resizeItems(ref, targetWidth) {
  if (!ref) return 0;
  const { width } = ref.getBoundingClientRect();
  const { mobile } = WindowSize;
  const itemGap = mobile ? 12 : 24;
  const itemsPerRow = Math.floor((width + itemGap) / (targetWidth + itemGap));
  const totalGap = itemGap * (itemsPerRow - 1);
  const newItemWidth = (width - totalGap) / itemsPerRow;
  return newItemWidth;
}
